import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MaterialMaster = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('material_master');
  const [viewItem, setViewItem] = useState(null);
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'material-types';

  const [materialTypes, setMaterialTypes] = useState([]);
  const [numberRanges, setNumberRanges] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMaterialType, setEditingMaterialType] = useState(null);
  const [selectedNumberRange, setSelectedNumberRange] = useState('');

  // Simple view checkboxes state - 8 views
  const [enabledViews, setEnabledViews] = useState({
    general_data: true,
    basic_data: true,
    sales: false,
    mrp: false,
    forecasting: false,
    plant_data_storage: false,
    production_resources_tools: false,
    accounting: false
  });

  useEffect(() => {
    if (activeTab === 'material-types') {
      loadMaterialTypes();
      loadNumberRanges();
    }
  }, [activeTab]);

  const loadMaterialTypes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/material-types`);
      setMaterialTypes(response.data || []);
    } catch (error) {
      console.error('Error loading material types:', error);
      toast.error('Failed to load material types');
    } finally {
      setIsLoading(false);
    }
  };

  const loadNumberRanges = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/number-ranges`);
      setNumberRanges(response.data || []);
    } catch (error) {
      console.error('Error loading number ranges:', error);
    }
  };

  const handleOpenDialog = (materialType = null) => {
    if (materialType) {
      setEditingMaterialType(materialType);
      setEnabledViews(materialType.enabled_views);
      setSelectedNumberRange(materialType.number_range_id || '');
    } else {
      setEditingMaterialType(null);
      setSelectedNumberRange('');
      setEnabledViews({
        general_data: true,
        basic_data: true,
        sales: false,
        mrp: false,
        forecasting: false,
        plant_data_storage: false,
        production_resources_tools: false,
        accounting: false
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      material_type_id: formData.get('material_type_id')?.toUpperCase() || editingMaterialType?.material_type_id,
      description: formData.get('description'),
      number_range_id: (selectedNumberRange && selectedNumberRange !== 'NONE') ? selectedNumberRange : null,
      enabled_views: enabledViews
    };

    try {
      if (editingMaterialType) {
        await axios.put(`${BACKEND_URL}/api/material-types/${editingMaterialType.material_type_id}`, {
          description: data.description,
          number_range_id: data.number_range_id,
          enabled_views: data.enabled_views
        });
        toast.success('Material Type updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/material-types`, data);
        toast.success('Material Type created successfully');
      }
      setIsDialogOpen(false);
      loadMaterialTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save material type');
    }
  };

  const handleViewChange = (viewName) => {
    setEnabledViews(prev => ({
      ...prev,
      [viewName]: !prev[viewName]
    }));
  };

  const views = [
    { key: 'general_data', label: 'General Data', locked: true },
    { key: 'basic_data', label: 'Basic Data', locked: true },
    { key: 'sales', label: 'Sales' },
    { key: 'mrp', label: 'MRP' },
    { key: 'forecasting', label: 'Forecasting' },
    { key: 'plant_data_storage', label: 'Plant Data / Storage' },
    { key: 'production_resources_tools', label: 'Production Resources / Tools' },
    { key: 'accounting', label: 'Accounting' }
  ];

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Material Master Configuration</h1>
          <p className="text-sm text-gray-500 mt-1">Configure material master settings</p>
        </div>

        {/* Material Types Tab */}
        {activeTab === 'material-types' && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Material Types</h2>
              {writable && (
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Add Material Type
              </Button>
              )}
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                <p className="mt-2 text-gray-500">Loading...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Material Type ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Number Range</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Enabled Views</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialTypes.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-8 text-gray-500">
                          No material types found. Click "Add Material Type" to create one.
                        </td>
                      </tr>
                    ) : (
                      materialTypes.map((mt) => {
                        // Count enabled views
                        const enabledCount = Object.values(mt.enabled_views).filter(v => v).length;
                        return (
                          <tr key={mt.material_type_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 font-mono text-sm font-semibold">{mt.material_type_id}</td>
                            <td className="py-3 px-4 text-sm">{mt.description}</td>
                            <td className="py-3 px-4 text-sm">
                              {mt.number_range_id ? (
                                <span className="px-2 py-1 text-xs font-medium rounded bg-orange-100 text-orange-700">
                                  {mt.number_range_id}
                                </span>
                              ) : (
                                <span className="text-gray-400">Manual</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm">
                              <span className="px-2 py-1 text-xs font-medium rounded bg-orange-100 text-orange-700">
                                {enabledCount} fields enabled
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex gap-2 justify-end">
                                <RecordActions
                                  doctype="material_master"
                                  onView={() => setViewItem(mt)}
                                  onEdit={() => handleOpenDialog(mt)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Add/Edit Material Type Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingMaterialType ? 'Edit Material Type' : 'Add Material Type'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Material Type ID *</Label>
                  <Input
                    name="material_type_id"
                    defaultValue={editingMaterialType?.material_type_id || ''}
                    placeholder="e.g., FERT, ROH, HALB"
                    maxLength={4}
                    disabled={!!editingMaterialType}
                    className="uppercase"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">4-character code (e.g., FERT for Finished Products)</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingMaterialType?.description || ''}
                    placeholder="e.g., Finished Products"
                    required
                  />
                </div>
                <div>
                  <Label>Number Range (Optional)</Label>
                  <Select value={selectedNumberRange || ''} onValueChange={setSelectedNumberRange}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select Number Range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">None (Manual Entry)</SelectItem>
                      {numberRanges.map((range) => (
                        <SelectItem key={range.number_range_id} value={range.number_range_id}>
                          {range.number_range_id} - {range.description} ({range.range_type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Select a number range or leave empty for manual entry</p>
                </div>
              </div>

              {/* Enabled Views */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-gray-700">Enabled Views</h3>
                <p className="text-xs text-gray-500">Select which views should be available when creating/editing materials of this type</p>
                
                <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {views.map((view) => (
                    <div key={view.key} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={view.key}
                        checked={enabledViews[view.key]}
                        onChange={() => handleViewChange(view.key)}
                        disabled={view.locked}
                        className="w-4 h-4"
                      />
                      <label
                        htmlFor={view.key}
                        className={`text-sm ${view.locked ? 'text-gray-500 font-semibold' : 'text-gray-700'}`}
                      >
                        {view.label}
                        {view.locked && ' (Always Enabled)'}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingMaterialType ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default MaterialMaster;
